import React, { useEffect } from 'react';
import styled from 'styled-components';
import Button from 'components/Button';
import { Form, Spin } from 'antd';
import TextInput from 'components/TextInput';
import DatePicker from 'components/DatePicker';
import request from 'config/request';
import endpoints from 'config/endpoints';
import showNotification from 'utils/notification';
import { SubscribeRequestParams } from 'utils/types';
import { getMicrositeSubscibrKey } from 'constant/constants';
import PhoneInput from 'react-phone-input-2';
import * as ga from 'lib/ga';
import { TRACKING } from 'constant/analytics';
import { REGION } from 'constant';
import { lowerCase } from 'lodash';
import Agreement from 'components/Agreement';

const trackingEvent = (event: any) => () => {
  ga.event(event);
};

interface SubscribeModalProps {
  theme: Record<any, any>;
  loading: boolean;
  subscribeData: Record<any, any>;
  slug: string;
  brandName: string;
  onClose: any;
  setIsSessionSubscribed: any;
  logo: string;
  brandRegion: string;
}
interface SubscribeModalWrapProps {
  $fontType: string;
  $hightlightColor: string;
  $fontColor: string;
}

interface SubscribeButtonWrapperProps {
  $fontFamily: string;
  $backgroundColor: string;
}

const SubscribeModal = ({
  theme,
  loading,
  subscribeData,
  slug,
  onClose,
  setIsSessionSubscribed,
  logo,
  brandRegion,
}: SubscribeModalProps) => {
  const [form] = Form.useForm();

  useEffect(() => {
    return () => {
      form.resetFields();
    };
  }, []);

  const {
    subscribeModalFontType,
    subscribeModalFontColor,
    subscribeModalHighlightColor,
    subscribeModalFooterButtonBackgroundColor,
    subscribeModalFooterButtonFontType,
  } = theme;

  const {
    SubscribeModalConfigTitle,
    SubscribeModalConfigBody,
    SubscribeModalConfigFooterButtonTitle,
    voucherDetails,
    hideLogo,
  } = subscribeData;

  if (loading) {
    return (
      <MicrositeLoader>
        <Spin />
      </MicrositeLoader>
    );
  }

  const handleSubmit = async (values: Record<string, any>) => {
    const { email, name, phoneNumber, dob } = values;
    let requestPayload: SubscribeRequestParams = {
      email,
    };
    if (name) {
      requestPayload = { ...requestPayload, name };
    }
    if (dob) {
      requestPayload = { ...requestPayload, dob };
    }
    if (phoneNumber) {
      requestPayload = { ...requestPayload, phoneNumber };
    }

    const requestUrl = await `${endpoints.momos.microsite.subscribe}`.replace(/:slug/g, slug);
    const response = await request.post(requestUrl, requestPayload);
    if (response?.data?.status) {
      const successMsg = voucherDetails
        ? `Your offer will be sent to your email shortly`
        : 'Stay tuned to hear more from us!';
      showNotification(successMsg, "Congratulations, you've subscribed", 'success');
      const brandMicrositeKey = getMicrositeSubscibrKey(slug);
      localStorage.setItem(
        brandMicrositeKey,
        JSON.stringify({
          email,
          slug,
        }),
      );
      onClose();
      form.resetFields();
      setIsSessionSubscribed({
        email,
        slug,
      });
    }
  };

  return (
    <Form layout="vertical" form={form} onFinish={handleSubmit}>
      <SubscribeModalWrap
        $fontColor={subscribeModalFontColor}
        $fontType={subscribeModalFontType}
        $hightlightColor={subscribeModalHighlightColor}
      >
        <div className="hero-container">
          <div className="modal-container">
            <div className="modal-bottom-container">
              {logo && hideLogo && <img src={logo} className="microsite-desk-logo" />}
              <div className="bottom-heading-container">
                <p className="heading-text mb-0" style={{ color: subscribeModalFontColor }}>
                  {SubscribeModalConfigTitle || 'Subscribe'}
                </p>
                <p className="body-text mb-0">{SubscribeModalConfigBody || ''}</p>
              </div>
              <div className="form-container">
                {subscribeData?.subscribeModalFields
                  ?.filter((each: Record<string, any>) => each?.status)
                  ?.map((eachField: Record<string, any>) => {
                    if (eachField?.type === 'email') {
                      return (
                        <Form.Item
                          rules={[{ required: eachField?.isRequired, type: 'email', message: 'Please enter email' }]}
                          name="email"
                          className="input-container input-label  mb-0"
                          label="Email"
                          key={eachField.name}
                        >
                          <TextInput
                            type="email"
                            placeholder="Email"
                            onChange={trackingEvent(TRACKING.HOME.SUBSCRIBE.TYPE_EMAIL)}
                            onClick={trackingEvent(TRACKING.HOME.SUBSCRIBE.EMAIL)}
                          />
                        </Form.Item>
                      );
                    } else if (eachField?.type === 'text') {
                      return (
                        <Form.Item
                          rules={[{ required: eachField?.isRequired, message: 'Please enter your name' }]}
                          name="name"
                          className="input-container input-label mb-0"
                          label="First/Last name"
                          key={eachField.name}
                        >
                          <TextInput
                            placeholder="First/Last name"
                            onChange={trackingEvent(TRACKING.HOME.SUBSCRIBE.TYPE_FIRST_LAST_NAME)}
                            onClick={trackingEvent(TRACKING.HOME.SUBSCRIBE.FIRST_LAST_NAME)}
                          />
                        </Form.Item>
                      );
                    } else if (eachField?.type === 'tel') {
                      return (
                        <Form.Item
                          label="Phone Number"
                          name="phoneNumber"
                          className="input-container input-label  mb-0"
                          rules={[
                            {
                              required: eachField?.isRequired,
                              message: 'Invalid Phone Number!',
                            },
                            {
                              min: 8,
                              message: 'Invalid Phone Number!',
                            },
                          ]}
                          key={eachField.name}
                        >
                          <PhoneInput
                            country={lowerCase(brandRegion) || lowerCase(REGION)}
                            inputClass={'momos-input'}
                            onChange={trackingEvent(TRACKING.HOME.SUBSCRIBE.TYPE_PHONE_NUMBER)}
                            onClick={trackingEvent(TRACKING.HOME.SUBSCRIBE.PHONE_NUMBER)}
                            countryCodeEditable={false}
                          />
                        </Form.Item>
                      );
                    } else if (eachField?.type === 'date') {
                      return (
                        <Form.Item
                          rules={[
                            {
                              required: eachField?.isRequired,
                              message: 'Invalid Date',
                              type: 'date',
                            },
                          ]}
                          label="Date of Birth"
                          name="dob"
                          className="input-container input-label  mb-0"
                          key={eachField.name}
                        >
                          <DatePicker
                            placeholder="Date Of Birth"
                            className="w-full"
                            overlappedFootnote=""
                            disabledFootnote={undefined}
                            format="YYYY-MM-DD"
                            onChange={trackingEvent(TRACKING.HOME.SUBSCRIBE.TYPE_DATE_OF_BIRTH)}
                            onClick={trackingEvent(TRACKING.HOME.SUBSCRIBE.DATE_OF_BIRTH)}
                          />
                        </Form.Item>
                      );
                    }
                  })}
                <Agreement slug={slug} />

                <SubscribeButtonWrapper
                  $backgroundColor={subscribeModalFooterButtonBackgroundColor}
                  $fontFamily={subscribeModalFooterButtonFontType}
                >
                  <Button
                    key="save"
                    type="primary"
                    // disabled={!agreement.momosTerms || !agreement.brandTerms}
                    onClick={() => {
                      ga.event(TRACKING.HOME.SUBSCRIBE.SUBSCRIBE_NOW);
                      form.submit();
                    }}
                    style={{
                      backgroundColor: subscribeModalFooterButtonBackgroundColor,
                      fontFamily: subscribeModalFooterButtonFontType,
                      borderColor: subscribeModalFooterButtonBackgroundColor,
                      borderRadius: 500,
                    }}
                  >
                    {SubscribeModalConfigFooterButtonTitle || 'Subscribe'}
                  </Button>
                </SubscribeButtonWrapper>
              </div>
            </div>
          </div>
        </div>
      </SubscribeModalWrap>
    </Form>
  );
};

export default SubscribeModal;

const SubscribeModalWrap = styled.div<SubscribeModalWrapProps>`
  height: 100%;
  overflow: hidden;
  overflow-y: auto;

  ::-webkit-scrollbar {
    width: 4px;
    height: 8px;
    background-color: transparent;
  }

  ::-webkit-scrollbar-thumb {
    background: var(--grey-light);
  }

  ::selector {
    background: ${(props) => props.$hightlightColor};
  }

  .hero-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 15px;
    .modal-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      border-radius: 10px;
      width: 100%;
      background: var(--white);
    }

    .modal-top-container {
      display: flex;
      flex-direction: row;
      justify-content: flex-end;
      align-items: flex-start;
      padding: 30px 10px 20px 10px;
      gap: 0px;
      width: 100%;
    }

    .modal-bottom-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 10px;
      background: var(--white);
      width: 100%;
      border-radius: 10px;

      .microsite-desk-logo {
        display: block;
        margin: 0 auto;
        width: 100px;
        height: 100px;
        object-fit: contain;
        margin-bottom: 10px;
      }

      .bottom-heading-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 10px;

        .heading-text {
          font-family: ${(props) => props.$fontType};
          color: ${(props) => props.$fontColor};
          font-style: normal;
          font-weight: 700;
          font-size: 24px;
          line-height: 120%;
          display: flex;
          align-items: center;
          text-align: center;
          margin-bottom: 0px;
        }

        .body-text {
          font-family: ${(props) => props.$fontType};
          color: ${(props) => props.$fontColor};
          font-style: normal;
          font-weight: 500;
          font-size: 16px;
          line-height: 120%;
          text-align: center;
          margin-bottom: 0px;
        }
      }

      .form-container {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        padding: 0px;
        gap: 15px;
        width: 100%;

        .ant-form-item {
          margin-bottom: 0px !important;
        }

        .input-container {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          width: 100%;
          cursor: pointer;

          .react-tel-input {
            input {
              font-family: ${(props) => props.$fontType};
            }

            .form-control {
              width: 100%;
              height: 50px;
            }

            .flag-dropdown {
              border-radius: 550px 0 0 550px;
            }

            .selected-flag {
              border-radius: 550px 0 0 550px;
            }
            .country-list {
              text-align: left;
            }
          }

          .ant-picker {
            width: 100%;
          }
          .ant-form-item-row {
            width: 100%;
          }

          .ant-form-item-control {
            width: 100%;
          }
          .ant-form-item-label > label {
            font-style: normal;
            font-weight: 500;
            font-size: 14px;
            line-height: 120%;
            font-family: ${(props) => props.$fontType};
            color: ${(props) => props.$fontColor};
          }

          .ant-picker .input-label {
            font-family: ${(props) => props.$fontType};
            color: ${(props) => props.$fontColor};
          }

          .momos-input {
            font-family: ${(props) => props.$fontType};
            color: ${(props) => props.$fontColor};
          }
        }

        .country-code-select {
          .ant-select-selector {
            width: 100px;
            padding-left: 0px;
            border: none;

            .ant-select-selection-search {
              width: 100%;
              left: 0px;
            }
          }
        }

        .button-container {
          width: 100%;
        }
      }
    }
  }
`;

const MicrositeLoader = styled.div`
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SubscribeButtonWrapper = styled.div<SubscribeButtonWrapperProps>`
  width: 100%;
  margin-top: 10px;
  .ant-btn-primary {
    font-family: ${(props) => props.$fontFamily};
    background: ${(props) => props.$backgroundColor};
    border-color: ${(props) => props.$backgroundColor};
    width: 100%;
    &:hover {
      filter: brightness(90%);
    }
  }

  .ant-btn span {
    font-family: ${(props) => props.$fontFamily};
    font-size: 16px;
    line-height: 20px;
  }
`;
